import { assign, zipObject, map, some, pickBy, flatten, filter, uniq } from "../../utils/lodash";
import * as Evaluator from "../../expressions/parser";
import { RecallState } from "../../models/states/RecallState";
import { InterviewState } from "../../models/states/InterviewState";
import { generateId } from "./utilities";
import { RecallType } from "../../piping/RecallType";
import { PageModel } from "../../models/PageModel";
import { getVariableRange } from "../../models/utilities/questionnaireUtilities";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import {
    CACHE_RECALL,
    SET_VARIABLE_MENTIONS,
    QUESTIONNAIRE_CHANGED,
    DEVICE_CHANGED,
    RESPONDENT_CHANGED,
    RESPONDENT_SET_EMAIL,
    RESPONDENT_SET_PHONE,
    RESPONDENT_SET_TIMEZONE,
    SET_LOOP_ROW,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_PREVIOUS_PAGE,
    NAVIGATION_BRANCH_TO,
    NAVIGATION_RESET,
    CHANGE_LANGUAGE,
    SERVER_PAGE_CHANGED,
    LOAD_ANSWERS_DTO,
    SET_COORDINATES,
    RESPONDENT_SET_DISPOSITIONCODE,
} from "../actions";
import { ActionTypes } from "../actions/types";
import { BranchToOptions } from "../../models/BranchToOptions";

const INITIAL_STATE: RecallState = {
    recalledTextById: {},
    recalledTextIdsByVariableName: {},
    recalledTextIdByKey: {},
};

export const cacheKey = (expression: string, mode: RecallType) => `${mode}|${expression}`;

export const getRecall = (
    state: RecallState,
    expression: string,
    mode: RecallType = RecallType.Text,
): string => {
    if (expression === undefined) return undefined;
    return state.recalledTextById[state.recalledTextIdByKey[cacheKey(expression, mode)]];
};

const cacheRecall = (
    state: RecallState,
    expression: string,
    mode: RecallType,
    result: string,
    variables: string[],
) => {
    const key = cacheKey(expression, mode);
    // Recall is already in the cache.
    if (state.recalledTextIdByKey[key] !== undefined) return state;
    // Generate a unique id.
    const id = generateId("recall");
    // Return new state.
    return {
        recalledTextById: {
            ...state.recalledTextById,
            [id]: result,
        },
        recalledTextIdsByVariableName: assign(
            {},
            state.recalledTextIdsByVariableName,
            zipObject(
                variables,
                map(variables, (v) => [...(state.recalledTextIdsByVariableName[v] || []), id]),
            ),
        ),
        recalledTextIdByKey: {
            ...state.recalledTextIdByKey,
            [key]: id,
        },
    };
};

const invalidateVariables = (state: RecallState, ...variables: string[]) => {
    Evaluator.invalidateCache(...variables);
    const a = map(variables, (v) => state.recalledTextIdsByVariableName[v.toUpperCase()] || []);
    const b = flatten(a);
    const ids = uniq(b) as number[];
    if (ids.length === 0) return state;
    return {
        recalledTextById: pickBy(
            state.recalledTextById,
            (value, key) => ids.indexOf(parseInt(key)) === -1,
        ),
        recalledTextIdsByVariableName: pickBy(
            state.recalledTextIdsByVariableName,
            (value, key) => !some(variables, (v) => v.toUpperCase() === key.toUpperCase()),
        ),
        recalledTextIdByKey: pickBy(
            state.recalledTextIdByKey,
            (value) => ids.indexOf(value) === -1,
        ),
    };
};

const getAutoCleanVariables = (
    branchTo: BranchToOptions,
    questionnaire: QuestionnaireModel,
    previousPage: PageModel,
) => {
    if (!branchTo.autoClean || previousPage === undefined) return [];
    const variablesToClean = getVariableRange(
        questionnaire,
        previousPage.questions.slice(-1)[0].name,
        branchTo.destination,
    );
    return variablesToClean.slice(1).map((s) => s.name);
};

const getLoopInvalidatedVariables = (
    questionnaire: QuestionnaireModel,
    currentPage: PageModel,
    previousPage: PageModel,
) => {
    if (
        currentPage.blockIndex === previousPage?.blockIndex &&
        currentPage.loopIndex !== previousPage?.loopIndex
    ) {
        const questionsToClean = questionnaire.blocks[currentPage.blockIndex].questions;
        const a = map(questionsToClean, (q) => q.variables.map((v) => v.name));
        const b = flatten(a);
        const c = filter(b, (v) => v !== undefined);
        const d = uniq(c);
        return map(d, (s: string) => s);
    }
    return [];
};

const TIME_VARS = ["$D", "$H", "$T", "$J"];

export const recallReducer = (
    state = INITIAL_STATE,
    action: ActionTypes,
    interview: InterviewState,
    previousPage: PageModel,
) => {
    switch (action.type) {
        case CACHE_RECALL:
            return cacheRecall(
                state,
                action.expression,
                action.mode,
                action.result,
                action.variables,
            );
        case SET_VARIABLE_MENTIONS:
            return invalidateVariables(state, action.variableName);
        case LOAD_ANSWERS_DTO:
            return invalidateVariables(state, ...action.answers.map((a) => a.variableName));
        case QUESTIONNAIRE_CHANGED:
            Evaluator.clearCache();
            return INITIAL_STATE;
        case DEVICE_CHANGED:
            return invalidateVariables(
                state,
                "$DEVICE",
                "$DEVICEOS",
                "$DEVICEOSVERSION",
                "$DEVICEBROWSER",
                "$DEVICEBROWSERVERSION",
            );
        case RESPONDENT_CHANGED:
            return invalidateVariables(state, ...TIME_VARS, "$Q", "$PIN", "$EMAIL", "$PHONE", "$L");
        case RESPONDENT_SET_EMAIL:
            return invalidateVariables(state, "$EMAIL");
        case RESPONDENT_SET_PHONE:
            return invalidateVariables(state, "$PHONE");
        case RESPONDENT_SET_TIMEZONE:
            return invalidateVariables(state, ...TIME_VARS);
        case RESPONDENT_SET_DISPOSITIONCODE:
            return invalidateVariables(state, "$R");
        case SET_LOOP_ROW:
            return invalidateVariables(state, "$R", "$ROW");
        case NAVIGATION_NEXT_PAGE:
        case NAVIGATION_PREVIOUS_PAGE:
        case SERVER_PAGE_CHANGED:
            return invalidateVariables(
                state,
                ...TIME_VARS,
                "$UTCDATE",
                "$UTCTIME",
                "$R",
                "$ROW",
                ...getLoopInvalidatedVariables(
                    interview.questionnaire,
                    interview.navigation.currentPage,
                    previousPage,
                ),
            );
        case NAVIGATION_BRANCH_TO:
            return invalidateVariables(
                state,
                ...getAutoCleanVariables(action.options, interview.questionnaire, previousPage),
                ...getLoopInvalidatedVariables(
                    interview.questionnaire,
                    interview.navigation.currentPage,
                    previousPage,
                ),
            );
        case NAVIGATION_RESET:
            if (action.clearAnswers) {
                Evaluator.clearCache();
                return INITIAL_STATE;
            }
            return state;
        case CHANGE_LANGUAGE:
            Evaluator.clearCache();
            return INITIAL_STATE;
        case SET_COORDINATES:
            return invalidateVariables(state, "$GEOLOCATION");
        default:
            return state;
    }
};
